































































































































































import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import SelectMemberRoleSetting from '@/components/select/select-member-role-setting.vue';
import DialogConfirm from '@/components/board/board-dialog-confirm.vue';
import InfoUser from '@/components/info/info-user.vue';
import PlusIcon from '@/components/icons/IconPlus.vue';
import ModalBox from './modal-box.vue';
import {
  Board,
  BoardInvitationRequest,
  BoardMember,
  BoardMemberRole,
  InviteReturnType
} from '@/types/Board';
import { User } from '@/types/User';
import apis from '@/services/apis';
import { debounce } from 'lodash';

const { mapGetters } = createNamespacedHelpers('boards');
const {
  mapGetters: membersGetters,
  mapActions: membersActions
} = createNamespacedHelpers('members');

interface ThisComponent extends Vue {
  email: string;
  loading: boolean;
  boardId: number;
  fetchLoading: boolean;
  suggestion: Array<User>;
}
export default Vue.extend({
  components: {
    ModalBox,
    SelectMemberRoleSetting,
    InfoUser,
    PlusIcon
  },
  data() {
    return {
      userSelect: [] as User[],
      email: '',
      loading: false,
      fetchLoading: false,
      suggestion: [] as User[],
      inputExtended: false,
      editing: false
    };
  },
  computed: {
    ...mapGetters(['getCurrentUserMember', 'getActiveBoardId']),
    ...membersGetters({
      getMembers: 'getMembers',
      isNextValid: 'isNextValid',
      fetchMembersLoading: 'isLoading'
    }),
    members(): BoardMember[] {
      return this.getMembers;
    },
    unselectedSuggestion(): User[] {
      const userSelectIDs = this.userSelect.map(e => e.id);
      return this.suggestion.filter(
        option => !userSelectIDs.includes(option.id)
      );
    },
    boardId(): number {
      return parseInt(this.$route.params.id, 10) | this.getActiveBoardId;
    },
    board(): Board {
      return this.$store.getters['boards/getActiveBoard'];
    },
    isAdmin(): boolean {
      return (
        (this.getCurrentUserMember as BoardMember | undefined)?.role ===
        BoardMemberRole.Admin
      );
    },
    invalidInvite(): boolean {
      const addingUser = { email: this.email } as User;
      const users = [...this.userSelect];
      if (this.email) users.push(addingUser);
      return users.some(u => !this.validateEmail(u)) || users.length == 0;
    },
    isOnlyOneAdmin(): boolean {
      const isAdmin = (member: BoardMember): boolean => member.role === 100;
      return this.members.filter(isAdmin).length === 1;
    }
  },
  methods: {
    ...membersActions(['fetchMembers', 'inviteMember']),
    reset() {
      this.userSelect = [];
      this.inputExtended = false;
      this.clearEmail();
    },
    clearEmail() {
      this.email = '';
    },
    deselect(user: User) {
      this.userSelect = this.userSelect.filter(u => u.id !== user.id);
    },
    async addInviteEmail() {
      const email = this.email;
      const addNewUser = () => {
        const tempId = new Date().getTime();
        this.userSelect.push({
          email,
          id: tempId
        } as User);
      };
      const canAdd = !this.suggestion.length;
      if (canAdd) addNewUser();
      await this.fetchUsers();
      this.$nextTick(() => {
        this.clearEmail();
      });
    },
    async onInvite(req: BoardInvitationRequest) {
      const group = 'invite-member';
      this.loading = true;
      this.$notify({
        group,
        clean: true
      });
      try {
        await this.inviteMember(req);
        this.$notify({
          text: `${this.$t('notifications.popup.invite')}`,
          type: 'success',
          group
        });
      } catch {
        //
      }
      this.loading = false;
    },
    async submitEmail() {
      const emails = this.userSelect.map(e => e.email);
      if (this.email) emails.push(this.email);
      await this.onInvite({
        id: this.boardId,
        emails,
        role: 2,
        returnType: InviteReturnType.Email
      });
      this.userSelect = [];
      this.clearEmail();
    },
    onKickMember(member: BoardMember) {
      const boardName = this.board.name || '';
      this.$modal.show(
        DialogConfirm,
        {
          title: member.isCurrentUser
            ? this.$t('invite.alert.leave.name')
            : this.$t('invite.alert.remove.name'),
          description: member.isCurrentUser
            ? `${this.$t(
                'invite.alert.leave.title1'
              )} <strong>${boardName}</strong>?<br/>${this.$t(
                'invite.alert.leave.title2'
              )}`
            : `
          ${this.$t('invite.alert.remove.title1')} "<strong>${member.user
                .name || member.inviteToEmail}</strong>"? `,
          color: 'text-red-600',
          button: member.isCurrentUser
            ? this.$t('invite.alert.leave.button')
            : this.$t('invite.alert.remove.button'),
          confirmAction: 'members/kickMember',
          confirmData: member
        },
        {
          height: 'auto',
          width: 440
        }
      );
    },
    fetchUsers: debounce(async function(this: ThisComponent, email = '') {
      const params = { q: email, boardID: this.boardId, type: 1 };
      const { data } = await apis.get('/search/users', { params });
      const { entities } = data;
      this.suggestion = entities;
      this.fetchLoading = false;
    }, 100),
    resizeWidth(ev: InputEvent) {
      const target = ev.target as HTMLTextAreaElement;
      const { scrollHeight, clientHeight, clientWidth } = target;
      const inputExtended = scrollHeight > clientHeight;
      if (clientWidth < 70) this.inputExtended = true;
      if (inputExtended) this.inputExtended = inputExtended;
      if (!target.value) this.inputExtended = false;
    },
    async onSearch() {
      this.fetchLoading = true;
      this.suggestion = [];
      this.$nextTick(() => this.fetchUsers(this.email));
    },
    onSelect(user: User) {
      this.email = user.email;
    },
    onBottom() {
      if (!this.fetchMembersLoading && this.isNextValid) {
        this.fetchMembers();
      }
    },
    filterBy(option: User, label: string, search: string) {
      return label?.toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    filter(options: User[], search: string) {
      return options.filter(option => {
        const label = `${option.name} ${option.email}`;
        return this.filterBy(option, label, search);
      });
    },
    validateEmail(user: User) {
      const username = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*/;
      const domain = /(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
      const reValidEmail = RegExp(`${username.source}@${domain.source}`);
      return reValidEmail.test(user.email);
    },
    handleSelectMousedown(ev: MouseEvent) {
      const findParent = (
        selector: string,
        el: HTMLElement | null
      ): HTMLElement | null =>
        el
          ? el.parentElement?.classList.contains(selector)
            ? el.parentElement
            : findParent(selector, el.parentElement)
          : null;
      const target = ev.target as HTMLElement;
      const parent = findParent('vs__deselect', target);
      if (parent) ev.stopPropagation();
    },
    disabledSelectRole(member: BoardMember): boolean {
      return !member.isCurrentUser && this.isOnlyOneAdmin && !this.isAdmin;
    }
  }
});
